import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const TransactionCard = ({ transaction }) => {
  const navigate = useNavigate();

  const getTRLColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getTRLBgColor = (score) => {
    if (score >= 80) return 'bg-success/10';
    if (score >= 60) return 'bg-warning/10';
    return 'bg-error/10';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-success text-success-foreground';
      case 'Pending': return 'bg-warning text-warning-foreground';
      case 'Review': return 'bg-accent text-accent-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const handleViewDetails = () => {
    navigate(`/transaction-details/${transaction?.id}`);
  };

  const handleUploadDocument = () => {
    navigate('/document-management', { state: { transactionId: transaction?.id } });
  };

  const handleRequestSignature = () => {
    navigate('/digital-signature', { state: { transactionId: transaction?.id } });
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft hover:shadow-elevated transition-smooth overflow-hidden">
      {/* Property Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={transaction?.propertyImage}
          alt={transaction?.propertyTitle}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(transaction?.status)}`}>
            {transaction?.status}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <div className="bg-black/50 backdrop-blur-sm rounded-md px-2 py-1">
            <span className="text-white text-xs font-medium">{transaction?.propertyType}</span>
          </div>
        </div>
        {transaction?.isBlockchainVerified && (
          <div className="absolute bottom-3 right-3">
            <div className="bg-success/90 backdrop-blur-sm rounded-full p-1.5">
              <Icon name="Shield" size={16} color="white" />
            </div>
          </div>
        )}
      </div>
      {/* Card Content */}
      <div className="p-6">
        {/* Property Details */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-foreground mb-1 line-clamp-1">
            {transaction?.propertyTitle}
          </h3>
          <p className="text-sm text-muted-foreground flex items-center">
            <Icon name="MapPin" size={14} className="mr-1" />
            {transaction?.location}
          </p>
          <p className="text-lg font-bold text-primary mt-2">
            ${transaction?.value?.toLocaleString()}
          </p>
        </div>

        {/* TRL Score */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Transaction Readiness Level</span>
            <span className={`text-sm font-bold ${getTRLColor(transaction?.trlScore)}`}>
              {transaction?.trlScore}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                transaction?.trlScore >= 80 ? 'bg-success' :
                transaction?.trlScore >= 60 ? 'bg-warning' : 'bg-error'
              }`}
              style={{ width: `${transaction?.trlScore}%` }}
            />
          </div>
          <div className={`mt-2 px-3 py-2 rounded-md ${getTRLBgColor(transaction?.trlScore)}`}>
            <p className="text-xs text-foreground">
              {transaction?.trlDescription}
            </p>
          </div>
        </div>

        {/* Stakeholder Status */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-foreground mb-2">Stakeholder Progress</h4>
          <div className="grid grid-cols-2 gap-2">
            {transaction?.stakeholders?.map((stakeholder, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  stakeholder?.completed ? 'bg-success' : 'bg-muted'
                }`} />
                <span className="text-xs text-muted-foreground">{stakeholder?.role}</span>
                {stakeholder?.completed && (
                  <Icon name="Check" size={12} className="text-success" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-muted/50 rounded-md">
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Documents</p>
            <p className="text-sm font-semibold text-foreground">
              {transaction?.documentsCompleted}/{transaction?.documentsTotal}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Signatures</p>
            <p className="text-sm font-semibold text-foreground">
              {transaction?.signaturesCompleted}/{transaction?.signaturesRequired}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Days Left</p>
            <p className={`text-sm font-semibold ${
              transaction?.daysLeft <= 7 ? 'text-error' : 
              transaction?.daysLeft <= 14 ? 'text-warning' : 'text-foreground'
            }`}>
              {transaction?.daysLeft}
            </p>
          </div>
        </div>

        {/* Next Actions */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-foreground mb-2">Next Actions</h4>
          <div className="space-y-1">
            {transaction?.nextActions?.slice(0, 2)?.map((action, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Icon name="Clock" size={12} className="text-warning" />
                <span className="text-xs text-muted-foreground">{action}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <Button
            variant="default"
            size="sm"
            onClick={handleViewDetails}
            iconName="Eye"
            iconPosition="left"
            className="flex-1"
          >
            View Details
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleUploadDocument}
            iconName="Upload"
            iconPosition="left"
            className="flex-1"
          >
            Upload
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRequestSignature}
            className="flex-shrink-0"
          >
            <Icon name="PenTool" size={16} />
          </Button>
        </div>

        {/* Blockchain Timestamp */}
        {transaction?.lastBlockchainUpdate && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Icon name="Shield" size={12} className="text-success" />
              <span>Last blockchain update: {transaction?.lastBlockchainUpdate}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionCard;